/**
 * @module NotificationBell
 * @package @geenius/notifications-solidjs-css
 * @description Renders the bell trigger used to open the notification panel in
 * the SolidJS vanilla CSS variant.
 */

import type { JSX } from 'solid-js'
import { Show } from 'solid-js'
import { cx } from '../lib/cx'
import { createNotificationCopy } from '../lib/notificationCopy'

/**
 * Props for the SolidJS CSS notification bell trigger.
 */
export interface NotificationBellProps {
  unreadCount?: number
  onClick?: () => void
  isOpen?: boolean
  ariaControls?: string
  class?: string
}

/**
 * Renders the bell button used to toggle the notification panel.
 *
 * @param props Bell props including unread count and optional open state.
 * @returns The bell trigger element.
 */
export function NotificationBell(props: NotificationBellProps): JSX.Element {
  const { plural, t } = createNotificationCopy()
  const count = () => props.unreadCount ?? 0
  const accessibleCount = () =>
    count() > 99
      ? t('notifications.bell.countOverflowAria')
      : plural('notifications.bell.countAria', count())

  return (
    <button
      type="button"
      class={cx('notifications__bell', props.class)}
      onClick={() => props.onClick?.()}
      aria-label={count() > 0 ? accessibleCount() : t('notifications.bell.openAria')}
      aria-haspopup="dialog"
      aria-expanded={props.isOpen ?? false}
      aria-controls={props.ariaControls}
    >
      <svg
        aria-hidden="true"
        class="notifications__bell-icon"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      <Show when={count() > 0}>
        <span class="notifications__badge" aria-hidden="true">
          {count() > 99 ? '99+' : count()}
        </span>
      </Show>
    </button>
  )
}
